import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { SimulationStatus } from '../../types/simulation-results.ts';
import type { RootState } from '../index.ts';
import { loadScenario } from '../actions/loadScenario.ts';

import type { AssertionDiagnosis } from '../../types/assertion-diagnostics.ts';

export interface AssertionResult {
  assertionId: string;
  assertionType: string;
  passed: boolean;
  /** True when the assertion was not evaluated (reason in detail). `passed` is false. */
  skipped?: boolean;
  detail: string;
  /** Structured diagnostic for failures — actionable intelligence for the user. */
  diagnosis?: AssertionDiagnosis;
}

export interface AssertionSummary {
  total: number;
  passed: number;
  failed: number;
  /** Assertions that could not be evaluated — explicit, never silent. */
  skipped: number;
  byType: Record<string, { passed: number; failed: number; skipped: number }>;
}

export function emptyAssertionSummary(): AssertionSummary {
  return { total: 0, passed: 0, failed: 0, skipped: 0, byType: {} };
}

export interface CumulativeCoverage {
  nodes: string[];
  /** Frame-scoped branch route keys: `${frameId}:${condition}`. */
  branches: string[];
  scenarios: string[];
}

/** A branch decision observed during a run — one per branch frame taken. */
export interface BranchActivationRecord {
  frameId: string;
  /** The branch condition (predicate route key) observed on the event. */
  condition: string;
  /** The mapped route value (predicate.routes[condition]). */
  route: string;
}

export type ScenarioVerdict = 'pass' | 'fail' | 'error' | 'inconclusive';

export interface SuiteResult {
  scenarioId: string;
  scenarioLabel: string;
  verdict: ScenarioVerdict | 'skipped';
  /** Present for 'skipped' and 'error' rows — why the scenario has no clean verdict. */
  reason?: string;
  assertionSummary: AssertionSummary;
  durationMs: number;
}

export interface SimulationState {
  currentNode: string | null;
  activeNodes: string[];
  visitedNodes: string[];
  activatedBranches: string[];
  /** Structured branch decisions for this run (frame-scoped, condition + route). */
  activatedBranchRecords: BranchActivationRecord[];
  activeConnections: string[];
  walkerPosition: number;
  actualPath: string[];
  simulationStatus: SimulationStatus;
  assertionResults: AssertionResult[];
  assertionSummary: AssertionSummary;
  /** Snapshot of assertion results preserved across visual replay. */
  assertionResultsSnapshot: AssertionResult[] | null;
  assertionSummarySnapshot: AssertionSummary | null;
  /** The 0-based event index the inspector panel should display. */
  activeEventIndex: number | null;
  /** Coverage accumulated across multiple scenario runs. */
  cumulativeCoverage: CumulativeCoverage;
  /** Results from batch suite runs. */
  suiteResults: SuiteResult[];
  /** Whether a suite run is in progress. */
  suiteRunning: boolean;
}

const initialState: SimulationState = {
  currentNode: null,
  activeNodes: [],
  visitedNodes: [],
  activatedBranches: [],
  activatedBranchRecords: [],
  activeConnections: [],
  walkerPosition: 0,
  actualPath: [],
  simulationStatus: 'idle',
  assertionResults: [],
  assertionSummary: emptyAssertionSummary(),
  assertionResultsSnapshot: null,
  assertionSummarySnapshot: null,
  activeEventIndex: null,
  cumulativeCoverage: { nodes: [], branches: [], scenarios: [] },
  suiteResults: [],
  suiteRunning: false,
};

type AssertionOutcome = 'passed' | 'failed' | 'skipped';

function outcomeOf(result: Pick<AssertionResult, 'passed' | 'skipped'>): AssertionOutcome {
  if (result.skipped) return 'skipped';
  return result.passed ? 'passed' : 'failed';
}

function bumpSummary(
  summary: AssertionSummary,
  assertionType: string,
  outcome: AssertionOutcome,
  delta: 1 | -1,
): void {
  summary.total += delta;
  summary[outcome] += delta;
  if (!summary.byType[assertionType]) {
    summary.byType[assertionType] = { passed: 0, failed: 0, skipped: 0 };
  }
  summary.byType[assertionType][outcome] += delta;
}

/**
 * Record an assertion result, replacing any previous record with the same
 * assertionId (scrub/replay may re-execute steps — records are keyed by id
 * so counts never double).
 */
function recordAssertion(state: SimulationState, result: AssertionResult): void {
  const existingIdx = state.assertionResults.findIndex((r) => r.assertionId === result.assertionId);
  if (existingIdx >= 0) {
    const existing = state.assertionResults[existingIdx];
    bumpSummary(state.assertionSummary, existing.assertionType, outcomeOf(existing), -1);
    state.assertionResults[existingIdx] = result;
  } else {
    state.assertionResults.push(result);
  }
  bumpSummary(state.assertionSummary, result.assertionType, outcomeOf(result), 1);
}

const simulationSlice = createSlice({
  name: 'simulation',
  initialState,
  reducers: {
    simulationStarted(state) {
      // Snapshot assertion results before clearing so the panel stays stable
      if (state.assertionResults.length > 0) {
        state.assertionResultsSnapshot = state.assertionResults;
        state.assertionSummarySnapshot = state.assertionSummary;
      }
      state.simulationStatus = 'running';
      state.currentNode = null;
      state.activeNodes = [];
      state.visitedNodes = [];
      state.activatedBranches = [];
      state.activatedBranchRecords = [];
      state.activeConnections = [];
      state.walkerPosition = 0;
      state.actualPath = [];
      state.assertionResults = [];
      state.assertionSummary = emptyAssertionSummary();
      state.activeEventIndex = null;
    },

    simulationStepCompleted(
      state,
      action: PayloadAction<{
        nodeId: string;
        connectionIds?: string[];
        branchActivated?: string;
        branchRecord?: BranchActivationRecord;
      }>,
    ) {
      const { nodeId, connectionIds, branchActivated, branchRecord } = action.payload;

      // Move previous currentNode to visited
      if (state.currentNode && !state.visitedNodes.includes(state.currentNode)) {
        state.visitedNodes.push(state.currentNode);
      }

      state.currentNode = nodeId;
      state.activeNodes = [nodeId];
      state.actualPath.push(nodeId);
      // walkerPosition counts EVENTS processed (path + non-path); it must be
      // incremented, not assigned from actualPath.length — the two index
      // spaces diverge whenever non-path events (SimSaga.*) are interleaved.
      state.walkerPosition += 1;
      state.activeEventIndex = state.walkerPosition - 1;

      if (connectionIds) {
        state.activeConnections = connectionIds;
      }

      if (branchActivated && !state.activatedBranches.includes(branchActivated)) {
        state.activatedBranches.push(branchActivated);
      }

      if (
        branchRecord &&
        !state.activatedBranchRecords.some(
          (r) => r.frameId === branchRecord.frameId && r.condition === branchRecord.condition,
        )
      ) {
        state.activatedBranchRecords.push(branchRecord);
      }
    },

    /** Advance walkerPosition without modifying actualPath (for non-path events). */
    advanceWalkerPosition(state) {
      state.walkerPosition += 1;
      state.activeEventIndex = state.walkerPosition - 1;
    },

    simulationPaused(state) {
      if (state.simulationStatus === 'running') {
        state.simulationStatus = 'paused';
      }
    },

    simulationResumed(state) {
      if (state.simulationStatus === 'paused') {
        state.simulationStatus = 'running';
      }
    },

    simulationCompleted(state) {
      state.simulationStatus = 'completed';
      state.activeNodes = [];
      state.activeConnections = [];
      // Clear snapshot — live results are now complete
      state.assertionResultsSnapshot = null;
      state.assertionSummarySnapshot = null;
    },

    simulationFailed(state) {
      state.simulationStatus = 'failed';
    },

    resetSimulation() {
      return initialState;
    },

    assertionPassed(
      state,
      action: PayloadAction<{
        assertionId: string;
        assertionType: string;
        detail: string;
      }>,
    ) {
      const { assertionId, assertionType, detail } = action.payload;
      recordAssertion(state, { assertionId, assertionType, passed: true, detail });
    },

    assertionFailed(
      state,
      action: PayloadAction<{
        assertionId: string;
        assertionType: string;
        detail: string;
        diagnosis?: AssertionDiagnosis;
      }>,
    ) {
      const { assertionId, assertionType, detail, diagnosis } = action.payload;
      recordAssertion(state, { assertionId, assertionType, passed: false, detail, diagnosis });
    },

    /** Record an assertion that could NOT be evaluated — explicit, never silent. */
    assertionSkipped(
      state,
      action: PayloadAction<{
        assertionId: string;
        assertionType: string;
        detail: string;
      }>,
    ) {
      const { assertionId, assertionType, detail } = action.payload;
      recordAssertion(state, { assertionId, assertionType, passed: false, skipped: true, detail });
    },

    /**
     * Backward scrub — restores the RECORDED path truncated to `position`
     * (an EVENT index). `actualPath` is the truncated recorded walk, never
     * fabricated from expectedPath. Forward scrubs replay real steps via the
     * walker saga instead of dispatching this action.
     */
    scrubToPosition(
      state,
      action: PayloadAction<{
        actualPath: string[];
        position: number;
        activatedBranches?: string[];
        activatedBranchRecords?: BranchActivationRecord[];
        activeConnections?: string[];
      }>,
    ) {
      const { actualPath, position, activatedBranches, activatedBranchRecords, activeConnections } =
        action.payload;
      state.actualPath = [...actualPath];
      state.walkerPosition = Math.max(0, position);
      state.visitedNodes = actualPath.slice(0, Math.max(0, actualPath.length - 1));
      state.currentNode = actualPath.length > 0 ? actualPath[actualPath.length - 1] : null;
      state.activeNodes = state.currentNode ? [state.currentNode] : [];
      state.activeEventIndex = position > 0 ? position - 1 : null;

      // Restore branch and connection state when provided (bidirectional scrub)
      if (activatedBranches !== undefined) {
        state.activatedBranches = activatedBranches;
      }
      if (activatedBranchRecords !== undefined) {
        state.activatedBranchRecords = activatedBranchRecords;
      }
      if (activeConnections !== undefined) {
        state.activeConnections = activeConnections;
      }
    },

    setActiveEventIndex(state, action: PayloadAction<number | null>) {
      state.activeEventIndex = action.payload;
    },

    accumulateCoverage(
      state,
      action: PayloadAction<{ scenarioId: string; nodes: string[]; branches: string[] }>,
    ) {
      const { scenarioId, nodes, branches } = action.payload;
      if (!state.cumulativeCoverage.scenarios.includes(scenarioId)) {
        state.cumulativeCoverage.scenarios.push(scenarioId);
      }
      for (const n of nodes) {
        if (!state.cumulativeCoverage.nodes.includes(n)) {
          state.cumulativeCoverage.nodes.push(n);
        }
      }
      for (const b of branches) {
        if (!state.cumulativeCoverage.branches.includes(b)) {
          state.cumulativeCoverage.branches.push(b);
        }
      }
    },

    resetCumulativeCoverage(state) {
      state.cumulativeCoverage = { nodes: [], branches: [], scenarios: [] };
    },

    suiteStarted(state) {
      state.suiteRunning = true;
      state.suiteResults = [];
    },

    /**
     * Drop suite rows without starting a run. Dispatched when the active
     * source's data changes identity (source switch, new upload, new live
     * session): rows keyed by another manifest's scenarioIds are stale truth
     * and must not gate or leak into the next ledger's auto-replay.
     */
    clearSuiteResults(state) {
      state.suiteResults = [];
    },

    suiteScenarioCompleted(state, action: PayloadAction<SuiteResult>) {
      state.suiteResults.push(action.payload);
    },

    suiteFinished(state) {
      state.suiteRunning = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadScenario, (state) => {
      // Reset simulation to idle — walker saga picks up loadScenario
      // and calls simulationStarted() to transition to "running".
      // Preserve cumulative coverage and suite results across scenario switches.
      return {
        ...initialState,
        cumulativeCoverage: state.cumulativeCoverage,
        suiteResults: state.suiteResults,
        suiteRunning: state.suiteRunning,
      };
    });
  },
});

export const {
  simulationStarted,
  simulationStepCompleted,
  advanceWalkerPosition,
  simulationPaused,
  simulationResumed,
  simulationCompleted,
  simulationFailed,
  resetSimulation,
  assertionPassed,
  assertionFailed,
  assertionSkipped,
  scrubToPosition,
  setActiveEventIndex,
  accumulateCoverage,
  resetCumulativeCoverage,
  suiteStarted,
  suiteScenarioCompleted,
  suiteFinished,
  clearSuiteResults,
} = simulationSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectCurrentNode = (state: RootState) => state.simulation.currentNode;
export const selectActiveNodes = (state: RootState) => state.simulation.activeNodes;
export const selectVisitedNodes = (state: RootState) => state.simulation.visitedNodes;
export const selectActivatedBranches = (state: RootState) => state.simulation.activatedBranches;
export const selectActivatedBranchRecords = (state: RootState) =>
  state.simulation.activatedBranchRecords;
export const selectActiveConnections = (state: RootState) => state.simulation.activeConnections;
export const selectWalkerPosition = (state: RootState) => state.simulation.walkerPosition;
export const selectActualPath = (state: RootState) => state.simulation.actualPath;
export const selectSimulationStatus = (state: RootState) => state.simulation.simulationStatus;
export const selectAssertionResults = (state: RootState) => state.simulation.assertionResults;
export const selectAssertionSummary = (state: RootState) => state.simulation.assertionSummary;

/** Effective selectors — prefer snapshot (during replay) over live results. */
export const selectEffectiveAssertionResults = (state: RootState) =>
  state.simulation.assertionResultsSnapshot ?? state.simulation.assertionResults;
export const selectEffectiveAssertionSummary = (state: RootState) =>
  state.simulation.assertionSummarySnapshot ?? state.simulation.assertionSummary;

export const selectActiveEventIndex = (state: RootState) => state.simulation.activeEventIndex;

/** Check if a node is visited — handles both scenario and topology IDs. */
export const selectIsNodeVisited = (state: RootState, nodeId: string): boolean => {
  const { visitedNodes } = state.simulation;
  if (visitedNodes.includes(nodeId)) return true;
  // Check reverse: if nodeId is a topology ID, look for its scenario equivalent
  const scenarioId = state.topology.topologyToScenarioNodeMap[nodeId];
  if (scenarioId && visitedNodes.includes(scenarioId)) return true;
  return false;
};

/** Check if a node is active — handles both scenario and topology IDs. */
export const selectIsNodeActive = (state: RootState, nodeId: string): boolean => {
  const { activeNodes } = state.simulation;
  if (activeNodes.includes(nodeId)) return true;
  const scenarioId = state.topology.topologyToScenarioNodeMap[nodeId];
  if (scenarioId && activeNodes.includes(scenarioId)) return true;
  return false;
};

// ---------------------------------------------------------------------------
// Assertion helpers
// ---------------------------------------------------------------------------

/** Extract step index from assertionId conventions used in assertionEngine.ts */
export function parseStepIndex(assertionId: string): number | null {
  // path-step-{N}, boundary-{N}-*, schema-{N}-*, branch-eval-{N}-*, terminal-{N}-*,
  // topology-conformance-{N}. N is a PATH-CURSOR index (position in expectedPath).
  const m = assertionId.match(
    /^(?:path-step|boundary|schema|branch-eval|terminal|topology-conformance)-(\d+)/,
  );
  if (m) return parseInt(m[1], 10);
  // path-sequence has no step index — navigate to the last step
  if (assertionId === 'path-sequence') return null;
  return null;
}

/** True for events that never occupy a path step (SimSaga.* / SimFailure.*). */
function isNonPathEventType(eventType: string): boolean {
  return eventType.startsWith('SimSaga.') || eventType.startsWith('SimFailure.');
}

/**
 * Map the active EVENT index to the highest PATH-CURSOR index reached.
 * Assertion ids are path-cursor-indexed while activeEventIndex is
 * event-indexed; the two spaces diverge when non-path events interleave.
 */
export const selectPathCursorAtActiveEvent = createSelector(
  [(state: RootState) => state.events.events, selectActiveEventIndex],
  (events, activeIdx): number | null => {
    if (activeIdx === null) return null;
    let pathEvents = 0;
    const end = Math.min(activeIdx, events.length - 1);
    for (let i = 0; i <= end; i++) {
      if (!isNonPathEventType(events[i].eventType)) pathEvents++;
    }
    return pathEvents - 1; // highest path-step index shown so far (-1 = none)
  },
);

/** Assertion counts: step-indexed assertions up to the active step, plus all non-step assertions when complete. */
export const selectAssertionCountsAtStep = createSelector(
  [selectEffectiveAssertionResults, selectPathCursorAtActiveEvent, selectSimulationStatus],
  (
    results,
    pathCursor,
    status,
  ): { passed: number; failed: number; skipped: number; total: number } => {
    if (pathCursor === null) return { passed: 0, failed: 0, skipped: 0, total: 0 };
    const isComplete = status === 'completed' || status === 'failed';
    let passed = 0;
    let failed = 0;
    let skipped = 0;
    for (const r of results) {
      const step = parseStepIndex(r.assertionId);
      // Include step-indexed assertions up to the current path cursor,
      // plus all non-step assertions when simulation is complete
      if ((step !== null && step <= pathCursor) || (step === null && isComplete)) {
        if (r.skipped) skipped++;
        else if (r.passed) passed++;
        else failed++;
      }
    }
    return { passed, failed, skipped, total: passed + failed + skipped };
  },
);

/** All assertion results grouped by assertionType (uses effective/snapshot). */
export const selectAssertionResultsByType = createSelector(
  [selectEffectiveAssertionResults],
  (results): Record<string, AssertionResult[]> => {
    const grouped: Record<string, AssertionResult[]> = {};
    for (const r of results) {
      if (!grouped[r.assertionType]) grouped[r.assertionType] = [];
      grouped[r.assertionType].push(r);
    }
    return grouped;
  },
);

/** Failed assertions grouped by the node where they occurred. */
export const selectFailuresByNode = createSelector(
  [selectAssertionResults, selectActualPath],
  (results, actualPath): Record<string, AssertionResult[]> => {
    const grouped: Record<string, AssertionResult[]> = {};
    for (const r of results) {
      if (r.passed || r.skipped) continue;
      // Assertion ids and actualPath share the PATH-CURSOR index space
      // (both count path events only), so indexing actualPath is sound.
      const stepIndex = parseStepIndex(r.assertionId);
      const nodeId = stepIndex !== null ? (actualPath[stepIndex] ?? 'unknown') : 'global';
      if (!grouped[nodeId]) grouped[nodeId] = [];
      grouped[nodeId].push(r);
    }
    return grouped;
  },
);

// ---------------------------------------------------------------------------
// Verdict — derived from run status + assertion summary
// ---------------------------------------------------------------------------

/**
 * Verdict semantics:
 * - 'error'        — the run aborted/failed; assertions are incomplete and
 *                    MUST NOT read as a pass.
 * - 'inconclusive' — the run completed but nothing was actually evaluated
 *                    (no assertions, or all skipped).
 * - 'pass'/'fail'  — the run completed with real evaluations.
 * - null           — the run has not finished.
 */
export function computeScenarioVerdict(
  status: SimulationStatus,
  summary: AssertionSummary,
): ScenarioVerdict | null {
  if (status === 'failed') return 'error';
  if (status !== 'completed') return null;
  if (summary.passed + summary.failed === 0) return 'inconclusive';
  return summary.failed === 0 ? 'pass' : 'fail';
}

export const selectScenarioVerdict = createSelector(
  [selectSimulationStatus, selectEffectiveAssertionSummary],
  (status, summary): ScenarioVerdict | null => computeScenarioVerdict(status, summary),
);

// ---------------------------------------------------------------------------
// Coverage selectors
// ---------------------------------------------------------------------------

export const selectCumulativeCoverage = (state: RootState) => state.simulation.cumulativeCoverage;

export const selectCoverageSummary = createSelector(
  [
    selectCumulativeCoverage,
    (state: RootState) => state.topology.frames,
    (state: RootState) => state.topology.branchPredicates,
  ],
  (coverage, frames, branchPredicates) => {
    if (!frames || frames.length === 0) return null;
    const allNodeIds = new Set<string>();
    for (const frame of frames) {
      for (const node of frame.nodes) {
        allNodeIds.add(node.id);
      }
    }
    const totalNodes = allNodeIds.size;
    const coveredNodes = coverage.nodes.filter((n) => allNodeIds.has(n)).length;

    // Branch denominator counts per-branch-point ROUTES (frame-scoped keys
    // `frameId:condition`), so full coverage is reachable and the covered
    // count can never exceed the total. Covered branches are intersected
    // with the CURRENT topology's route keys (like nodes) so coverage
    // accumulated on other flows never leaks in (M7 cross-flow bug).
    const allBranchKeys = new Set<string>();
    if (branchPredicates) {
      for (const [frameId, predicate] of Object.entries(branchPredicates)) {
        for (const condition of Object.keys(predicate.routes)) {
          allBranchKeys.add(`${frameId}:${condition}`);
        }
      }
    }
    const totalBranches = allBranchKeys.size;
    const coveredBranches = coverage.branches.filter((b) => allBranchKeys.has(b)).length;
    return {
      totalNodes,
      coveredNodes,
      totalBranches,
      coveredBranches,
      scenarioCount: coverage.scenarios.length,
    };
  },
);

// ---------------------------------------------------------------------------
// Suite selectors
// ---------------------------------------------------------------------------

export const selectSuiteResults = (state: RootState) => state.simulation.suiteResults;
export const selectSuiteRunning = (state: RootState) => state.simulation.suiteRunning;

export default simulationSlice.reducer;
